import React from 'react';
import { Empty } from 'antd';

import { useUIColors } from '../../theme';
import { useUIText } from '../../i18n';
import type { Styleable } from '../../variants';

export interface EmptyStateProps extends Styleable {
    title?: React.ReactNode;
    description?: React.ReactNode;
    icon?: React.ReactNode;
    action?: React.ReactNode;
}

/**
 * Tier-1 presentational primitive: a domain-free "no data" placeholder for a
 * list, table, or section with nothing to show. Built on antd's `Empty` —
 * every list/table surface already renders its empty state through `Empty`
 * (see `DataTable`'s `locale.emptyText`), so this component gives the same
 * primitive a standalone wrapper with room for a supporting description and
 * an action slot (e.g. "Create item"). Unset `title` falls back to
 * `common.noData` from the shared i18n catalog; callers can override it via
 * props.
 */
export function EmptyState({
    title,
    description,
    icon,
    action,
    className,
    style,
}: EmptyStateProps): React.ReactElement {
    const text = useUIText();
    const colors = useUIColors();

    return (
        <div role="status" aria-live="polite" className={className} style={style}>
            <Empty image={icon} description={title ?? text.common.noData}>
                {description && (
                    <div style={{ color: colors.textMuted, marginTop: -8 }}>{description}</div>
                )}
                {action}
            </Empty>
        </div>
    );
}

export default EmptyState;
